import toast, {ToastType} from '@/components/Toast';
import {copyText} from '@/utils/commonUtils';
import type {HistoryInterface} from '@/types/document/doc';
import {scrollIntoBlock} from '@/rte/editor';
import {parse, stringify, removeSearchParamNoRefresh} from '@/utils/kuQuery';
import KU_QUERY from '@/constants/common/searchParamKeys';
import {feLogger, recordDocCardView, reportBodyOverFlow} from '@/buried';
import {
    EDITOR_TEXT_MAX_WIDTH,
    EDITOR_TEXT_MAX_WIDTH_KU_SHARE,
    EDITOR_TEXT_MAX_WIDTH_CARD_VIEW,
    ENVIRONMENT_LEVEL,
} from '@/Constant';
import * as store from '@/store';
import {getIdgWhiteBookTool} from '@baidu/ku-tool-sdk/dist/extension-loader';
import sdk from '@/sdk';

export enum CopyScene {
    Repository = 1,
    Document = 2,
}

/**
 * 生成短链接
 * @param scene 使用场景，包括生成知识库短链接、生成文档短链接
 * @param id 链接 id，知识库场景 - repositoryGuid，文档场景 - docGuid
 */
export function copyShortLink(scene: CopyScene, id: string | undefined) {
    if (!id) {
        toast({
            type: ToastType.Error,
            content: '复制失败',
        });
        return;
    }
    const prefix = scene === CopyScene.Repository ? '/r' : '/d';
    const url = `${window.location.origin}${prefix}/${id}`;

    try {
        copyText(url);
        toast({
            type: ToastType.Success,
            content: '短链接复制成功，已放入到剪贴板中',
        });
    } catch {
        toast({
            type: ToastType.Error,
            content: '复制失败',
        });
    }
}

/**
 * 更新 History 中的配置属性
 */
export function updateHistoryState(history: any, state: HistoryInterface) {

    const {location} = history;

    removeSearchParamNoRefresh(KU_QUERY.document.editor);
    removeSearchParamNoRefresh(KU_QUERY.document.isNewDoc);
    // 需要保留的参数
    const query = parse(window.location.search);
    const search = stringify(query);

    history.replace({
        ...location,
        search,
        state: {
            ...location.state,
            ...state,
        },
    });
}

export function scrollIntoBlockAndTips(options: Parameters<typeof scrollIntoBlock>[0]) {
    const result = scrollIntoBlock(options);
    if (result === false) {
        toast({
            type: ToastType.Error,
            content: '此内容未发布或已被删除',
        });
    }
}

export function scrollIntoComment(commentId: string) {
    scrollIntoBlock({commentId});
}

/**
 * 判断是否为新文档场景
 */
export const isNewDocSceneObj = {
    value: false,
    getVal() {
        return isNewDocSceneObj.value || false;
    },
    setVal(val: boolean) {
        isNewDocSceneObj.value = val;
    },
};

/**
 * 打开文档，若为卡片视图上报场景
 */
export const docCardViewBuriedObj = {
    prevDocGuid: '',
    report(docGuild: string, cardNum: number = -1) {
        if (docGuild === docCardViewBuriedObj.prevDocGuid) {
            return;
        }

        docCardViewBuriedObj.prevDocGuid = docGuild;
        feLogger(recordDocCardView({
            docId: docGuild || '',
            cardNum,
        }));
    },
    clearCache() {
        docCardViewBuriedObj.prevDocGuid = '';
    },
};

/**
 * 获取文档最大宽度
 * @param isWideScreen 是否为宽屏（自适应宽度）
 * @param isCardView 是否为卡片视图
 * @param isKuShare 是否在 Ku 享中
 * @returns 最大宽度样式设置
 */
export const getDocMaxWidth = (isWideScreen: boolean, isCardView: boolean, isKuShare: boolean) => {
    if (isWideScreen) {
        return 'none';
    }

    if (isCardView) {
        return EDITOR_TEXT_MAX_WIDTH_CARD_VIEW;
    }

    if (isKuShare) {
        return EDITOR_TEXT_MAX_WIDTH_KU_SHARE;
    }

    return EDITOR_TEXT_MAX_WIDTH;
};

let scrollBarDetectedAt: number | null = null; // 发现存在滚动条时刻
const detectionInterval = 3000; // 每 3000ms 检查一次
const requiredDuration = 3000; // 滚动条存在超过 3000ms 则认为存在
export const bodyScrollObj = {
    interval: null as NodeJS.Timeout | null,
    checkBodyScroll: () => {
        const body = document.body;
        const html = document.documentElement;

        // 获取body和html的scrollHeight和clientHeight
        const bodyScrollHeight = body.scrollHeight;
        const bodyClientHeight = body.clientHeight;
        const htmlScrollHeight = html.scrollHeight;
        const htmlClientHeight = html.clientHeight;

        // 判断是否有滚动条
        const bodyHasScrollbar = bodyScrollHeight > bodyClientHeight;
        const htmlHasScrollbar = htmlScrollHeight > htmlClientHeight;

        if (bodyHasScrollbar || htmlHasScrollbar) {
            if (!scrollBarDetectedAt) {
                scrollBarDetectedAt = Date.now();
            } else if (Date.now() - scrollBarDetectedAt >= requiredDuration) {
                const header = document.getElementById('rlkbHeader');
                const sideBar = document.getElementById('sideBar');
                const knowledgeContent = document.getElementById('knowledgeContent');
                const waterMarkContainer = document.getElementById('waterMarkContainer');

                const repParams = {
                    scrollTarget: bodyHasScrollbar ? 'body' : 'html',
                    scrollHeight: {
                        bodyScrollHeight,
                        bodyClientHeight,
                        htmlScrollHeight,
                        htmlClientHeight,
                        bodyScrollWidth: body.scrollWidth,
                        bodyClientWidth: body.clientWidth,
                        htmlScrollWidth: html.scrollWidth,
                        htmlClientWidth: html.clientWidth,
                    },
                    checkElement: {
                        headerScrollHeight: header?.scrollHeight,
                        headerClientHeight: header?.clientHeight,
                        sideBarScrollHeight: sideBar?.scrollHeight,
                        sideBarClientHeight: sideBar?.clientHeight,
                        knowledgeContentScrollHeight: knowledgeContent?.scrollHeight,
                        knowledgeContentClientHeight: knowledgeContent?.clientHeight,
                        waterMarkContainerScrollHeight: waterMarkContainer?.scrollHeight,
                        waterMarkContainerClientHeight: waterMarkContainer?.clientHeight,
                        other: [] as any,
                    },
                    isDemoMode: store.getData(store.KEYS.DEMO_MODE),
                    mode: store.getData(store.KEYS.DOCUMENT_MODE),
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                };

                // 检测 html 和 body 直接子元素的高度
                try {
                    // 获取所有直接子元素
                    const htmlChildren = Array.from(html.children);
                    const bodyChildren = Array.from(body.children);

                    // 创建一个数组，用来存储符合条件的子元素的信息
                    const filteredChildren: any = [];

                    // 遍历 html 的子元素
                    htmlChildren.forEach((child) => {
                        if (child.tagName === 'BODY') {
                            return;
                        }

                        if (child.clientHeight !== 0 || child.scrollHeight !== 0) {
                            filteredChildren.push({
                                parent: 'html',
                                tagName: child.tagName,
                                id: child.id,
                                className: child.className,
                                clientHeight: child.clientHeight,
                                scrollHeight: child.scrollHeight,
                            });
                        }
                    });

                    // 遍历 body 的子元素
                    bodyChildren.forEach((child) => {
                        if (child.clientHeight !== 0 || child.scrollHeight !== 0) {
                            filteredChildren.push({
                                parent: 'body',
                                tagName: child.tagName,
                                id: child.id,
                                className: child.className,
                                clientHeight: child.clientHeight,
                                scrollHeight: child.scrollHeight,
                            });
                        }
                    });

                    repParams.checkElement.other = filteredChildren;
                } catch (error: any) {
                    repParams.checkElement.other = error?.message;
                }

                bodyScrollObj.stopCheck();

                // 北极星上报参数
                feLogger(reportBodyOverFlow({
                    params: JSON.stringify(repParams),
                }));
            }
        } else {
            scrollBarDetectedAt = null; // 重置发现存在滚动条时刻
        }
    },
    startCheck: () => {
        bodyScrollObj.interval = setInterval(bodyScrollObj.checkBodyScroll, detectionInterval);
    },
    stopCheck: () => {
        bodyScrollObj.interval && clearInterval(bodyScrollObj.interval);
        bodyScrollObj.interval = null;
    },
};

/**
 * 文档流程图是否全量加载
 * 默认是 false，即不全量加载（按需加载）
 */
export function getDiagramLoadAll() {
    let res = false;

    try {
        const curRepository = store.getData(store.KEYS.CUR_REPOSITORY) || {};
        const IdgWhiteBookTool = getIdgWhiteBookTool(process.env.REACT_APP_ENVIRONMENT);
        res = (IdgWhiteBookTool.allowRepoList ?? []).includes(curRepository.repositoryGuid);
    } catch (e) {
        console.error(e);
        res = false;
    }

    return res;
}

/**
 * 文档思维导图是否全量加载
 * 默认是 false，即不全量加载（按需加载）
 */
export function getMindMapLoadAll() {
    let res = false;

    try {
        const curRepository = store.getData(store.KEYS.CUR_REPOSITORY) || {};
        const IdgWhiteBookTool = getIdgWhiteBookTool(process.env.REACT_APP_ENVIRONMENT);
        res = (IdgWhiteBookTool.allowRepoList ?? []).includes(curRepository.repositoryGuid);
    } catch (e) {
        console.error(e);
        res = false;
    }

    return res;
}

/**
 * 空白父节点引导缓存场景
 */
export const tipStorageObj = {
    limit: 50,
    storageKey: 'emptyParentNodeTip',
    get: () => {
        const data = localStorage.getItem(tipStorageObj.storageKey);

        return data ? JSON.parse(data) : [];
    },
    set: () => {
        const userGuid = sdk.as.user?.userGuid;
        const curDocument = store.getCurDocument();

        if (!userGuid || !curDocument || tipStorageObj.isCacheClose()) {
            return;
        }

        let data = tipStorageObj.get();
        data.push({
            userGuid,
            docGuid: curDocument.docGuid,
        });

        if (data.length > tipStorageObj.limit) {
            data = data.slice(data.length - tipStorageObj.limit);
        }

        localStorage.setItem(tipStorageObj.storageKey, JSON.stringify(data));
    },
    isCacheClose: () => {
        const userGuid = sdk.as.user?.userGuid;
        const curDocument = store.getCurDocument();

        if (!userGuid || !curDocument) {
            return;
        }

        const data = tipStorageObj.get();
        return data.some((item: any) => item.userGuid === userGuid && item.docGuid === curDocument.docGuid);
    },
};


/**
 * 拆卡推荐引导缓存场景
 */
export const pullCardStorageObj = {
    limit: 50,
    storageKey: 'pullCardTip',
    get: () => {
        const data = localStorage.getItem(pullCardStorageObj.storageKey);

        return data ? JSON.parse(data) : [];
    },
    set: () => {
        const userGuid = sdk.as.user?.userGuid;
        const curDocument = store.getCurDocument();

        if (!userGuid || !curDocument || pullCardStorageObj.isCacheClose()) {
            return;
        }

        let data = pullCardStorageObj.get();
        data.push({
            userGuid,
            docGuid: curDocument.docGuid,
        });

        if (data.length > pullCardStorageObj.limit) {
            data = data.slice(data.length - pullCardStorageObj.limit);
        }

        localStorage.setItem(pullCardStorageObj.storageKey, JSON.stringify(data));
    },
    isCacheClose: () => {
        const userGuid = sdk.as.user?.userGuid;
        const curDocument = store.getCurDocument();

        if (!userGuid || !curDocument) {
            return;
        }

        const data = pullCardStorageObj.get();
        return data.some((item: any) => item.userGuid === userGuid && item.docGuid === curDocument.docGuid);
    },
};

interface MappingPair {
    toc: string; // TOC 环境域名
    int: string; // 知识库内网环境域名
}

// 双向映射表（A ↔ B）
const mappingPairs: MappingPair[] = [
    {toc: 'ku.baidu.com', int: 'ku.baidu-int.com'},
    {toc: 'kugray.baidu.com', int: 'wikigray.baidu-int.com'},
    {toc: 'ku-qa3-external.dev.weiyun.baidu.com', int: 'ku-qa3.dev.weiyun.baidu.com'},
    {toc: 'ku-qa4-external.dev.weiyun.baidu.com', int: 'ku-qa4.dev.weiyun.baidu.com'},
];

/**
 * 内链跳转地址转换
 * 自动在 TOC 与 知识库环境之间切换域名
 * @param url 待转换链接
 */
export const transformHyperlink = (url: string): string => {
    try {
        const parsedUrl = new URL(url);
        const {host} = parsedUrl;
        const isTocPage = window.isTocPage;

        for (const {toc, int} of mappingPairs) {
            // 当前为 TOC 环境 -> 转到知识库域名
            if (isTocPage && host === int) {
                parsedUrl.host = toc;
                return parsedUrl.href;
            }
            // 当前为知识库环境 -> 转到 TOC 域名
            if (!isTocPage && host === toc) {
                parsedUrl.host = int;
                return parsedUrl.href;
            }
        }
        return url;
    } catch (error) {
        console.error('transformHyperlink error:', error);
        return url;
    }
};
